import { type ComponentPropsWithRef, type CSSProperties, type ReactNode, useId } from "react";
import { cx } from "../../lib/cx";
import styles from "./ProgressBar.module.css";

export type ProgressTone = "accent" | "success" | "warning" | "danger";

export interface ProgressBarProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
  /** Accessible name; also shown above the bar unless `hideLabel`. */
  label: ReactNode;
  hideLabel?: boolean;
  /** Current value; omit (or `null`) for indeterminate progress. */
  value?: number | null;
  min?: number;
  max?: number;
  /** Human-readable value, e.g. "42 of 120 MB". Defaults to a percentage. */
  valueText?: string;
  /** Show the value text next to the label. */
  showValue?: boolean;
  tone?: ProgressTone;
  size?: "sm" | "md";
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Determinate or indeterminate progress with native `progressbar` semantics.
 * Determinate bars expose `aria-valuenow/min/max` and `aria-valuetext`;
 * indeterminate ones omit the value and are marked busy.
 */
export function ProgressBar({
  label,
  hideLabel = false,
  value,
  min = 0,
  max = 100,
  valueText,
  showValue = false,
  tone = "accent",
  size = "md",
  className,
  style,
  ...rest
}: ProgressBarProps) {
  const labelId = useId();
  const indeterminate = value === undefined || value === null || Number.isNaN(value);
  const current = indeterminate ? undefined : clamp(value, min, max);
  const percent =
    current === undefined || max === min ? 0 : Math.round(((current - min) / (max - min)) * 100);
  const text = indeterminate ? valueText : (valueText ?? `${percent}%`);
  const showHeader = !hideLabel || (showValue && text);

  return (
    <div
      {...rest}
      className={cx(styles.root, className)}
      data-tone={tone}
      data-size={size}
      style={{ "--progress": `${percent}%`, ...style } as CSSProperties}
    >
      {showHeader && (
        <div className={styles.header}>
          <span id={labelId} className={cx(styles.label, hideLabel && "wv-visually-hidden")}>
            {label}
          </span>
          {showValue && text && (
            <span className={styles.value} aria-hidden="true">
              {text}
            </span>
          )}
        </div>
      )}
      {!showHeader && (
        <span id={labelId} className="wv-visually-hidden">
          {label}
        </span>
      )}
      <div
        role="progressbar"
        aria-labelledby={labelId}
        aria-valuemin={indeterminate ? undefined : min}
        aria-valuemax={indeterminate ? undefined : max}
        aria-valuenow={current}
        aria-valuetext={text}
        aria-busy={indeterminate || undefined}
        className={styles.track}
        data-indeterminate={indeterminate || undefined}
      >
        <div className={styles.fill} />
      </div>
    </div>
  );
}
